import React from 'react';
import { Phone, MessageCircle, ClipboardList, Heart, CheckCircle, ArrowRight } from 'lucide-react';
import { BackButton } from '../components/common/BackButton';
import { CTAButton } from '../components/common/CTAButton';
import { Helmet } from 'react-helmet-async';

export function LeadRescueSystemPage() {
  return (
    <div className="pt-20 min-h-screen">
      <Helmet>
        <title>Missed Call & Lead Rescue System - Priority AI Services</title>
        <meta
          name="description"
          content="Stop losing new clients to missed calls and forgotten messages. Simple SMS follow-up system for local service businesses - 24-hour implementation."
        />
        <link rel="canonical" href="https://priorityaiservices.ca/lead-rescue-system" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <BackButton label="Back" />
      </div>

      {/* Hero Section */}
      <section className="py-10 bg-black/50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Missed Call & Lead Rescue System
          </h1>
          <p className="text-xl text-gray-300 mb-8">
            For Local Service Businesses Who Want to Stop Losing New Clients
          </p>
          <CTAButton href="/book-audit" variant="primary" icon>
            Book Your Free 15-Minute Audit
          </CTAButton>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-black/30 border border-blue-500/20 rounded-lg p-8">
            <h2 className="text-3xl font-bold text-white mb-6">The Problem</h2>
            <div className="space-y-4 text-gray-300">
              <p className="text-lg">
                You're busy helping clients during the day. A potential customer calls but you can't answer.
                Someone fills out a form on your website. By the time you get back to them, they've already
                booked with someone else.
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Missed calls during busy hours mean lost revenue</li>
                <li>Slow responses to website inquiries cost you clients</li>
                <li>No easy way to see who tried to reach you and when</li>
                <li>People forget they reached out and move on to competitors</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="py-16 bg-black/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-6 text-center">
            What the Lead Rescue System Includes
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-black/30 border border-blue-500/20 rounded-lg p-6">
              <Phone className="w-12 h-12 text-blue-500 mb-4" />
              <h3 className="text-xl font-bold text-white mb-3">Missed Call Text-Back</h3>
              <p className="text-gray-300 mb-4">
                When someone calls and you can't answer, they automatically get a friendly SMS within
                seconds with your booking link.
              </p>
              <ul className="text-sm text-gray-400 space-y-2">
                <li>• Dedicated forwarding number</li>
                <li>• Instant SMS response</li>
                <li>• Professional, friendly messaging</li>
              </ul>
            </div>

            <div className="bg-black/30 border border-blue-500/20 rounded-lg p-6">
              <MessageCircle className="w-12 h-12 text-blue-500 mb-4" />
              <h3 className="text-xl font-bold text-white mb-3">Website Inquiry SMS Reply</h3>
              <p className="text-gray-300 mb-4">
                Form submissions on your website trigger an immediate SMS to the person with your
                booking link so they can schedule right away.
              </p>
              <ul className="text-sm text-gray-400 space-y-2">
                <li>• Instant response to form fills</li>
                <li>• Direct booking link included</li>
                <li>• Inquiry logged for follow-up</li>
              </ul>
            </div>

            <div className="bg-black/30 border border-blue-500/20 rounded-lg p-6">
              <ClipboardList className="w-12 h-12 text-blue-500 mb-4" />
              <h3 className="text-xl font-bold text-white mb-3">Simple Lead Log & Visibility</h3>
              <p className="text-gray-300 mb-4">
                Every missed call and inquiry is recorded in a simple Google Sheet or basic CRM so you
                can see who tried to reach you and when.
              </p>
              <ul className="text-sm text-gray-400 space-y-2">
                <li>• Easy-to-review lead log</li>
                <li>• Timestamps and contact info</li>
                <li>• No complicated software to learn</li>
              </ul>
            </div>

            <div className="bg-black/30 border border-blue-500/20 rounded-lg p-6">
              <Heart className="w-12 h-12 text-blue-500 mb-4" />
              <h3 className="text-xl font-bold text-white mb-3">Gentle Follow-Up</h3>
              <p className="text-gray-300 mb-4">
                For people who inquire but don't book right away, we set up gentle follow-up
                messages to remind them and make booking easy.
              </p>
              <ul className="text-sm text-gray-400 space-y-2">
                <li>• Scheduled follow-up texts</li>
                <li>• Non-pushy, helpful reminders</li>
                <li>• Customizable timing and tone</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-12 text-center">
            How It Works
          </h2>

          <div className="space-y-8">
            <div className="flex gap-6 items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-black font-bold text-xl">
                1
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Quick Audit Call</h3>
                <p className="text-gray-300">
                  We spend 15 minutes looking at how calls, forms, and messages currently work in your
                  business. No pressure, no hard sell.
                </p>
              </div>
            </div>

            <div className="flex gap-6 items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-black font-bold text-xl">
                2
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">24-Hour Setup</h3>
                <p className="text-gray-300">
                  Once you decide to move forward, I connect the SMS layer around your existing booking
                  system. Most setups are done within 24 hours.
                </p>
              </div>
            </div>

            <div className="flex gap-6 items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-black font-bold text-xl">
                3
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">More Booked Clients</h3>
                <p className="text-gray-300">
                  People who couldn't reach you or forgot to book now get immediate, helpful responses.
                  You see every inquiry in your lead log.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What You Need Section */}
      <section className="py-16 bg-black/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">
            What You Need to Get Started
          </h2>

          <div className="bg-black/30 border border-blue-500/20 rounded-lg p-8">
            <ul className="space-y-4">
              {[
                'Your existing booking link (Calendly, Acuity, Jane App, etc.)',
                'Your main business phone number',
                'Your logo and any tone/style preferences',
                'Website URL if you want form inquiry capture'
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-blue-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-300">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">
            Simple, Transparent Pricing
          </h2>

          <div className="space-y-6">
            <div className="bg-black/30 border border-blue-500/20 rounded-lg p-6">
              <h3 className="text-xl font-bold text-white mb-3">One-Time Setup Fee</h3>
              <p className="text-3xl font-bold text-blue-500 mb-4">$749</p>
              <p className="text-gray-300">
                Covers implementation, testing, training, and all core features including gentle follow-up messaging.
              </p>
            </div>

            <div className="bg-black/30 border border-blue-500/30 rounded-lg p-6">
              <div className="flex items-center gap-2 mb-3">
                <h3 className="text-xl font-bold text-white">Monthly System Care Plan</h3>
                <span className="px-3 py-1 bg-blue-500 text-black text-xs font-bold rounded-full">
                  Recommended
                </span>
              </div>
              <p className="text-3xl font-bold text-blue-500 mb-4">$49/month</p>
              <ul className="space-y-2 text-gray-300 mb-4">
                <li>• Tool costs and hosting covered</li>
                <li>• Break/fix support if something stops working</li>
                <li>• Updates when you change your booking link</li>
                <li>• Simple lead log maintenance</li>
                <li>• Review request campaigns</li>
                <li>• Reactivation sequences for past clients</li>
                <li>• Quarterly optimization sessions</li>
              </ul>
              <p className="text-sm text-gray-400">Month-to-month, cancel anytime</p>
            </div>

          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-black/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">
            Common Questions
          </h2>

          <div className="space-y-6">
            {[
              {
                q: "Will my phone number change?",
                a: "No. You keep your existing number. We set up a dedicated forwarding number that routes to your current line and sends the SMS."
              },
              {
                q: "How long does implementation take?",
                a: "Most setups are completed within 24 hours once you provide your booking link, phone number, and messaging preferences."
              },
              {
                q: "How are the SMS messages written? Can I approve them first?",
                a: "We write a friendly, professional message based on your tone preferences. You review and approve all messaging before we go live."
              },
              {
                q: "What tools do you use behind the scenes?",
                a: "We use industry-standard SMS platforms and automation tools. You don't need to manage any of it - that's covered in the System Care Plan."
              },
              {
                q: "Do I need to cancel my current booking system?",
                a: "Not at all. The Lead Rescue System works alongside your existing booking software (Calendly, Acuity, Jane App, etc.). We just add the SMS layer."
              },
              {
                q: "What if something breaks or I need to change my booking link?",
                a: "That's what the System Care Plan covers. If anything stops working or you need updates, just let me know and I'll handle it."
              },
              {
                q: "Will these messages clash with my existing appointment reminders?",
                a: "No. The Lead Rescue messages only go to new inquiries and missed calls - people who haven't booked yet. Once they book, your regular reminder system takes over."
              },
              {
                q: "Can we add more automations later?",
                a: "Absolutely. Once your lead capture is solid, we can add review requests, reactivation campaigns, or custom workflows."
              },
              {
                q: "Is there a contract?",
                a: "The setup fee is one-time. The monthly System Care Plan is month-to-month with no long-term contract. Cancel anytime."
              },
              {
                q: "What happens if I stop working with you?",
                a: "You keep all the messaging content and strategy we created. The automated systems would stop running, but you own everything we built together."
              }
            ].map((faq, index) => (
              <div key={index} className="bg-black/30 border border-blue-500/20 rounded-lg p-6">
                <h3 className="text-lg font-bold text-white mb-3">{faq.q}</h3>
                <p className="text-gray-300">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Ready to Stop Losing Leads?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Book a free 15-minute audit. We'll look at how calls and messages work in your business
            and see if the Lead Rescue System makes sense for you.
          </p>
          <CTAButton href="/book-audit" variant="primary" icon>
            Book Your Free Audit Call
          </CTAButton>
          <p className="text-sm text-gray-400 mt-4">No pressure. No hard sell. Just a quick look at your setup.</p>
        </div>
      </section>
    </div>
  );
}
